import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, X } from "lucide-react";
import { ALL_CARE_PLAN_TYPES } from "@/components/RecordDialog";

/**
 * AI Assist button + dialog for Care Plan.
 * Uses backend `/care-plan-wave2/ai-draft` to produce a JSON HTML draft
 * that is merged (via `onApply`) into the RecordDialog values.
 */
export default function CarePlanAiAssist({ clientId, planType, onApply }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [ctx, setCtx] = useState("");

  const generate = async () => {
    if (!clientId) { toast.error("Assign a service user first"); return; }
    const type = planType || "Personal Support Plan";
    setBusy(true);
    setDraft(null);
    try {
      const r = await api.post("/care-plan-wave2/ai-draft", { client_id: clientId, plan_type: type, additional_context: ctx });
      if (r.data.ok) setDraft(r.data);
      else toast.error(r.data.error || "AI draft failed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const apply = () => {
    if (!draft?.draft) return;
    onApply?.(draft.draft);
    toast.success("Draft applied — review & save");
    setOpen(false);
    setDraft(null);
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="btn-ai-assist"
        className="h-8 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
      >
        <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Assist — Generate Draft
      </Button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4" data-testid="ai-assist-dialog" onClick={() => setOpen(false)}>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-md" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-slate-200 dark:border-slate-700 px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-violet-700 dark:text-violet-400 font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3" /> Ayobell Premium</div>
                <h2 className="font-heading text-lg font-semibold">AI Assist — Generate Draft Care Plan</h2>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-900" data-testid="ai-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-slate-600 dark:text-slate-300">
                The AI reads the service user&apos;s primary needs, active risks, medications, allergies and care flags to draft a person-centred first cut of a <strong>{planType || "care plan"}</strong>. You review and save.
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Additional context (optional)</span>
                <textarea
                  value={ctx}
                  onChange={(e) => setCtx(e.target.value)}
                  placeholder="e.g. Recent hospital discharge for infection · New PBS training completed by team · Family raised concerns about night care"
                  rows={3}
                  data-testid="ai-context-input"
                  className="w-full mt-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-2 text-sm"
                />
              </label>
              {!draft && (
                <Button onClick={generate} disabled={busy} data-testid="btn-ai-generate" className="w-full bg-violet-700 hover:bg-violet-800 text-white">
                  {busy ? "Drafting with Claude Sonnet 4.5…" : "✨ Generate Draft"}
                </Button>
              )}
              {draft?.draft && (
                <div className="space-y-3">
                  {["goals", "desired_outcomes", "interventions", "person_voice", "summary"].map((k) => draft.draft[k] ? (
                    <details key={k} open className="rounded-md border border-slate-200 dark:border-slate-700 p-3">
                      <summary className="text-xs font-semibold text-slate-700 dark:text-slate-200 cursor-pointer uppercase tracking-wide">{k.replace(/_/g, " ")}</summary>
                      <div
                        className="prose prose-sm dark:prose-invert max-w-none mt-2"
                        dangerouslySetInnerHTML={{ __html: draft.draft[k] }}
                        data-testid={`ai-preview-${k}`}
                      />
                    </details>
                  ) : null)}
                </div>
              )}
            </div>
            {draft?.draft && (
              <div className="border-t border-slate-200 dark:border-slate-700 px-5 py-3 flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => { setDraft(null); }} data-testid="btn-ai-regenerate">Regenerate</Button>
                <Button onClick={apply} data-testid="btn-ai-apply" className="bg-emerald-700 hover:bg-emerald-800 text-white">Apply to plan</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
